import React from 'react'

import {
  ParsedSegmentPath,
  TCoreWidgetFullyLoaded,
  WidgetType
} from 'types/GlobalWidget'
import OptionsMenu from 'components_new/molecules/OptionsMenu'
import { getOptions } from './utils'
import { useCommonAttributeOptions } from 'redux/hooks/AttributeOptions'
import { useTranslation } from 'language/translator'

interface SegmentMenuProps {
  anchorPosition: { top: number; left: number } | null
  customSegmentBy?: string | null
  dashboardId: string
  kpiOptionId?: string // for horizontal tables
  link?: string | null
  onClickExternalLink?: ((link: string | null) => void) | null
  onClose: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void
  segments: ParsedSegmentPath[]
  setCustomSegmentBy?:
    | ((attributeOptionId: string, segments: ParsedSegmentPath[]) => void)
    | null
  setDetailsDialog?:
    | ((segments: ParsedSegmentPath[], kpiOptionId?: string | null) => void)
    | null
  setFilters?: ((segments: ParsedSegmentPath[]) => void) | null
  widget: TCoreWidgetFullyLoaded
}

const SegmentMenu = React.forwardRef((props: SegmentMenuProps, ref) => {
  const {
    anchorPosition,
    customSegmentBy,
    dashboardId,
    kpiOptionId,
    link = null,
    onClickExternalLink,
    onClose,
    segments,
    setCustomSegmentBy,
    setDetailsDialog,
    setFilters,
    widget
  } = props
  const open = Boolean(anchorPosition)

  const { t } = useTranslation()

  const attributeOptions = useCommonAttributeOptions(
    widget,
    'segment_attribute'
  )

  const isHorizontalTable =
    widget.settings.type.selected === WidgetType.LIST &&
    widget.settings.horizontal

  // use kpis if table is horizontal
  const allowMenu = isHorizontalTable ? !!kpiOptionId : segments.length > 0

  // check for requirements
  if (!open || !allowMenu || !setFilters) {
    return null
  }

  // a pivot row carries both, and there it is the segment that was
  // clicked — its metric only heads the rows it expands into
  const title =
    segments.length > 0
      ? segments.map((segment) => segment.display_label).join(', ')
      : widget.settings.kpi_options.find((item) => item.id === kpiOptionId)
        ?.title

  const isPublic = location.pathname.includes('public')

  const options = getOptions(
    dashboardId,
    widget.settings,
    segments,
    kpiOptionId ?? null,
    attributeOptions,
    setFilters,
    t,
    setDetailsDialog,
    customSegmentBy,
    setCustomSegmentBy,
    onClickExternalLink,
    link,
    isPublic
  )

  return (
    <OptionsMenu
      anchorPosition={anchorPosition || undefined}
      anchorReference="anchorPosition"
      onClose={onClose}
      open={open}
      options={options}
      ref={ref}
      title={title}
    />
  )
})

export default SegmentMenu
